import CompanyLayout from '@/components/feature/CompanyLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const developerNavItems = [
  { path: '/api-reference', label: 'API Reference' },
  { path: '/sdks', label: 'SDKs' },
];

const endpoints = [
  { method: 'GET', path: '/api/v1/markets', descKey: 'apiReference.epMarkets', descDefault: 'List all available trading markets', auth: false },
  { method: 'GET', path: '/api/v1/markets/{symbol}/ticker', descKey: 'apiReference.epTicker', descDefault: 'Get 24h ticker data for a specific market', auth: false },
  { method: 'GET', path: '/api/v1/markets/{symbol}/orderbook', descKey: 'apiReference.epOrderbook', descDefault: 'Get orderbook depth for a specific market', auth: false },
  { method: 'GET', path: '/api/v1/markets/{symbol}/trades', descKey: 'apiReference.epTrades', descDefault: 'Get recent trades for a specific market', auth: false },
  { method: 'GET', path: '/api/v1/account', descKey: 'apiReference.epAccount', descDefault: 'Get account balance and positions', auth: true },
  { method: 'POST', path: '/api/v1/orders', descKey: 'apiReference.epOrders', descDefault: 'Place a new order', auth: true },
  { method: 'DELETE', path: '/api/v1/orders/{id}', descKey: 'apiReference.epCancelOrder', descDefault: 'Cancel an existing order', auth: true },
  { method: 'GET', path: '/api/v1/orders', descKey: 'apiReference.epOrdersList', descDefault: 'List active orders', auth: true },
  { method: 'GET', path: '/api/v1/fills', descKey: 'apiReference.epFills', descDefault: 'Get historical fills', auth: true },
  { method: 'GET', path: '/api/v1/funding-rates', descKey: 'apiReference.epFundingRates', descDefault: 'Get current funding rates for all markets', auth: false },
];

export default function ApiReferencePage() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const bgCard = isDark ? 'bg-[#0a0a0a]' : 'bg-white';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#888]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#555]' : 'text-[#999]';

  return (
    <CompanyLayout
      title={t('apiReference.title', 'API Reference')}
      subtitle={t('apiReference.subtitle', 'Complete REST API documentation for market data, trading, and account management')}
      category="Developers"
      navItems={developerNavItems}
    >
      {/* Base URL */}
      <div className={`p-5 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-sm font-bold uppercase tracking-wider mb-3 ${textMuted}`}>{t('apiReference.baseUrl', 'Base URL')}</h2>
        <code className={`block p-3 rounded-lg font-mono text-sm ${isDark ? 'bg-black/40' : 'bg-[#f0f0f0]'} ${textPrimary}`}>
          https://api.zhisu.trade/v1
        </code>
      </div>

      {/* Authentication */}
      <div className={`p-5 rounded-xl border ${borderLight} ${bgCard} mb-6`}>
        <h2 className={`text-lg font-bold mb-3 ${textPrimary}`}>{t('apiReference.authentication', 'Authentication')}</h2>
        <p className={`text-sm mb-3 leading-relaxed ${textSecondary}`}>
          {t('apiReference.authDesc', 'Public endpoints do not require authentication. Authenticated endpoints require an API key passed in the X-API-Key header.')}
        </p>
        <code className={`block p-3 rounded-lg font-mono text-sm ${isDark ? 'bg-black/40' : 'bg-[#f0f0f0]'} ${textPrimary}`}>
          curl -H &quot;X-API-Key: your_api_key_here&quot; https://api.zhisu.trade/v1/account
        </code>
      </div>

      {/* Endpoints */}
      <div className={`rounded-xl border ${borderLight} ${bgCard} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${borderLight} ${textMuted}`}>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.method', 'Method')}</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.endpoint', 'Endpoint')}</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.description', 'Description')}</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">{t('table.auth', 'Auth')}</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.map((ep) => (
                <tr key={ep.path} className={`border-b ${borderLight} last:border-0 hover:bg-[#00ff88]/5 transition-colors`}>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-medium ${ep.method === 'GET' ? 'bg-[#00ff88]/10 text-[#00ff88]' : ep.method === 'POST' ? 'bg-[#ffaa00]/10 text-[#ffaa00]' : 'bg-[#ff5566]/10 text-[#ff5566]'}`}>
                      {ep.method}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-mono text-xs ${textPrimary}`}>{ep.path}</td>
                  <td className={`px-4 py-3 ${textSecondary}`}>{t(ep.descKey, ep.descDefault)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${ep.auth ? 'text-[#ffaa00]' : textMuted}`}>
                      {ep.auth ? t('table.required', 'Required') : t('table.none', 'None')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rate Limits */}
      <div className={`mt-6 p-5 rounded-xl border ${borderLight} ${bgCard}`}>
        <h2 className={`text-lg font-bold mb-3 ${textPrimary}`}>{t('apiReference.rateLimits', 'Rate Limits')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className={`p-4 rounded-lg border ${borderLight} text-center`}>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('apiReference.public', 'Public')}</div>
            <div className={`text-xl font-bold ${textPrimary}`}>100 / min</div>
          </div>
          <div className={`p-4 rounded-lg border ${borderLight} text-center`}>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('apiReference.authenticated', 'Authenticated')}</div>
            <div className={`text-xl font-bold ${textPrimary}`}>300 / min</div>
          </div>
          <div className={`p-4 rounded-lg border ${borderLight} text-center`}>
            <div className={`text-xs uppercase tracking-wider mb-1 ${textMuted}`}>{t('apiReference.websocket', 'WebSocket')}</div>
            <div className={`text-xl font-bold ${textPrimary}`}>Unlimited</div>
          </div>
        </div>
      </div>
    </CompanyLayout>
  );
}